// PoScanDrafts — the holding cell, from the screen's side.
//
// Mannie's errand: photograph the purchase order at the receiving bench, review
// it at the desk. That only works if the reading survives the walk, so every
// scan-parse now stages what it read along with the pages it read from
// (migration 017), and this is where the desk finds them.
//
// TWO PIECES, both here because they share the one awkward thing: the page
// endpoint rides an Authorization header, so no <img src> can point at it and
// every thumbnail is a fetched blob whose object URL somebody has to revoke.
//
//   ScanDraftsPanel  the "waiting for review" list on the idle screen
//   ScanDraftPages   the page strip on the review surface
//
// COMMUNAL, deliberately: the list is not filtered to whoever pressed the
// shutter, because the bench and the desk are routinely different people.

import React, { useEffect, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import {
  draftPageUrl,
  isDraftImage,
  type ScanDraftSummary,
  type ScanDraftFile,
} from './poScanApi';

/**
 * "just now" / "23 min ago" / "3 hr ago" / "2 days ago".
 *
 * Coarse on purpose. The only question this list answers about time is "has
 * this been sitting here a worrying amount of time", and "23 minutes and 14
 * seconds ago" answers it worse than "23 min ago" does.
 */
export function ageLabel(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * One page thumbnail, fetched as a blob because the endpoint needs the Bearer
 * header. Revokes its own URL on unmount and whenever the page it points at
 * changes — an effect that did not would pin every page ever viewed.
 */
const DraftPageImage: React.FC<{
  draftId: number;
  page: number;
  alt: string;
  className: string;
  /** Which lane's file route to read from. Defaults to scan-to-stock's. */
  pageUrl?: (draftId: number, page: number) => Promise<string | null>;
}> = ({ draftId, page, alt, className, pageUrl = draftPageUrl }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    let minted: string | null = null;
    setUrl(null);
    (async () => {
      const next = await pageUrl(draftId, page);
      if (dead) {
        if (next) URL.revokeObjectURL(next);
        return;
      }
      minted = next;
      setUrl(next);
    })();
    return () => {
      dead = true;
      if (minted) URL.revokeObjectURL(minted);
    };
  }, [draftId, page, pageUrl]);

  if (!url) return <div className={`${className} bg-gray-900`} aria-hidden="true" />;
  return <img src={url} alt={alt} className={className} />;
};

/**
 * The pages behind a staged reading, on the review surface.
 *
 * This is what makes a desk review a REVIEW rather than a retype: a staged
 * document is a model's opinion about a piece of paper, and you cannot tell a
 * misread part number from a real one with nothing to check it against. By the
 * time the desk sees it the box is closed and on a shelf.
 *
 * A .pdf or .xlsx draft has no thumbnail to show, so it says what it has
 * instead of rendering a gray box that looks like a failure.
 */
export const ScanDraftPages: React.FC<{
  draftId: number;
  files: ScanDraftFile[];
  sourceFilename?: string | null;
  pageUrl?: (draftId: number, page: number) => Promise<string | null>;
  /** What this document is called in copy: 'scan' (default) or 'quote'. */
  noun?: string;
}> = ({ draftId, files, sourceFilename, pageUrl = draftPageUrl, noun = 'scan' }) => {
  const images = files.filter((f) => isDraftImage(f.mimetype));
  if (files.length === 0) return null;

  if (images.length === 0) {
    return (
      <p className="text-xs text-on-surface-variant">
        Read from {sourceFilename || 'the uploaded file'}, kept with this {noun}.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-on-surface-variant">
        {images.length} page{images.length === 1 ? '' : 's'}, as photographed. Tap one to see it full
        size.
      </p>
      <ul className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="scan-draft-pages">
        {images.map((file) => (
          <li key={file.page_number}>
            <a
              href={`#page-${file.page_number}`}
              onClick={(e) => {
                // The full-size view is the browser's own: open the blob in a
                // tab. Building a lightbox here would be a second image viewer
                // to maintain for a screen that already has one job.
                e.preventDefault();
                pageUrl(draftId, file.page_number).then((url) => {
                  if (url) window.open(url, '_blank', 'noopener');
                });
              }}
              className="block rounded border border-gray-700 overflow-hidden hover:border-primary"
              aria-label={`Page ${file.page_number}, full size`}
            >
              <DraftPageImage
                draftId={draftId}
                page={file.page_number}
                alt={`Page ${file.page_number}`}
                className="w-full h-20 object-cover"
                pageUrl={pageUrl}
              />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * The "waiting for review" list.
 *
 * PHONE AND DESK ARE BOTH FIRST-CLASS here, more than anywhere else in this
 * flow: this list IS the handoff point between them. On a phone each draft is a
 * card with a thumbnail and two full-width-friendly actions; on a desk the same
 * markup lays out as a row.
 */
const ScanDraftsPanel: React.FC<{
  drafts: ScanDraftSummary[];
  busyId: number | null;
  onReview: (id: number) => void;
  onDiscard: (id: number) => void;
  pageUrl?: (draftId: number, page: number) => Promise<string | null>;
  /**
   * The three strings that differ between lanes. Everything else on this list —
   * the layout, the age label, the two-step discard, the thumbnail lifecycle —
   * is the same errand and stays one implementation.
   */
  copy?: { untitled: string; empty: string; discardQuestion: string };
}> = ({
  drafts,
  busyId,
  onReview,
  onDiscard,
  pageUrl = draftPageUrl,
  copy = {
    untitled: 'Unread purchase order',
    empty: 'Nothing is waiting for review. Scans you start on a phone show up here.',
    discardQuestion: 'Throw this scan away? The reading and the photos go with it.',
  },
}) => {
  // Which draft is asking "are you sure". Held here rather than in a window
  // confirm() so the question can name the scan and read like the rest of the
  // screen.
  const [confirming, setConfirming] = useState<number | null>(null);

  if (drafts.length === 0) {
    return (
      <p className="text-xs text-on-surface-variant" data-testid="scan-drafts-empty">
        {copy.empty}
      </p>
    );
  }

  return (
    <ul className="space-y-2" data-testid="scan-drafts">
      {drafts.map((draft) => {
        const title = draft.po_number_guess || draft.vendor_guess || copy.untitled;
        const asking = confirming === draft.id;
        return (
          <li
            key={draft.id}
            data-testid={`scan-draft-${draft.id}`}
            className="rounded-lg border border-gray-700/60 bg-gray-900/40 p-2.5 flex items-start gap-3"
          >
            {draft.page_count > 0 && (
              <DraftPageImage
                draftId={draft.id}
                page={1}
                alt=""
                className="w-14 h-14 shrink-0 rounded object-cover bg-surface"
                pageUrl={pageUrl}
              />
            )}

            <div className="min-w-0 flex-1">
              <p className="font-mono text-sm text-primary truncate">{title}</p>
              <p className="text-xs text-on-surface-variant truncate">
                {draft.po_number_guess && draft.vendor_guess ? `${draft.vendor_guess} · ` : ''}
                {draft.page_count} page{draft.page_count === 1 ? '' : 's'}
              </p>
              <p className="text-[11px] text-on-surface-variant">
                {ageLabel(draft.created_at)}
                {draft.created_by_name ? `, by ${draft.created_by_name}` : ''}
              </p>

              {asking ? (
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs text-amber-200">
                    {copy.discardQuestion}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setConfirming(null);
                        onDiscard(draft.id);
                      }}
                      disabled={busyId === draft.id}
                      className={buttonClass('danger', 'sm')}
                    >
                      Discard it
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(null)}
                      className={buttonClass('quiet', 'sm')}
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onReview(draft.id)}
                    disabled={busyId === draft.id}
                    className={buttonClass('filled', 'sm')}
                  >
                    {busyId === draft.id ? 'Opening…' : 'Review'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(draft.id)}
                    className={buttonClass('quiet', 'sm')}
                    aria-label={`Discard ${title}`}
                  >
                    Discard
                  </button>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default ScanDraftsPanel;
